import { useMemo, useState, type ReactElement } from "react";
import { layout, space } from "@tokens/index";
import { Button, Text, useViewport } from "@primitives/index";
import { AdminSelect, Field, MutationError } from "../admin-ui";
import { usePushTeamToQueue, useWithdrawTeamFromQueue } from "../management-groups";
import { useMyQueues } from "../queues";
import type { QueueSummary } from "../queues";

interface TeamQueuePushPanelProps {
    readonly groupId: string;
    readonly teamId: string;
}

export function TeamQueuePushPanel({ groupId, teamId }: TeamQueuePushPanelProps): ReactElement {
    const { isCompact } = useViewport();
    const queuesQuery = useMyQueues();
    const push = usePushTeamToQueue();
    const withdraw = useWithdrawTeamFromQueue();
    const [queueId, setQueueId] = useState<string>("");

    // Personal queues are excluded server-side and offering them here just
    // wastes a click; pre-filter so the UI never even tempts the admin.
    const eligibleQueues: readonly QueueSummary[] = useMemo(
        () =>
            (queuesQuery.data?.items ?? []).filter(
                (q) => !q.isPersonal && q.isWorkable && q.status === "active",
            ),
        [queuesQuery.data],
    );

    const lastPush = push.data;
    const lastWithdraw = withdraw.data;

    return (
        <div style={{ display: "grid", gap: space[2], minWidth: 0 }}>
            <Text role="caption" tone="muted" style={{ maxWidth: layout.proseMaxWidth }}>
                Push every team member into a queue (or withdraw them). Personal queues are
                excluded.
            </Text>
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                    gap: space[2],
                }}
            >
                <div style={{ flex: isCompact ? "1 1 100%" : "1 1 26ch", minWidth: 0 }}>
                    <Field label="Queue">
                        {(id) => (
                            <AdminSelect
                                id={id}
                                value={queueId}
                                onChange={(e) => setQueueId(e.target.value)}
                                style={{ minWidth: isCompact ? "100%" : "26ch" }}
                            >
                                <option value="">Choose a queue…</option>
                                {eligibleQueues.map((q) => (
                                    <option key={q.id} value={q.id}>
                                        {q.name}
                                    </option>
                                ))}
                            </AdminSelect>
                        )}
                    </Field>
                </div>
                <div
                    style={{
                        display: "flex",
                        gap: space[2],
                        flex: isCompact ? "1 1 100%" : "0 0 auto",
                    }}
                >
                    <Button
                        variant="primary"
                        size="md"
                        disabled={queueId.length === 0}
                        busy={push.isPending}
                        onClick={() => push.mutate({ id: groupId, teamId, queueId })}
                        {...(isCompact ? { style: { flex: 1 } } : {})}
                    >
                        Push
                    </Button>
                    <Button
                        variant="ghost"
                        size="md"
                        disabled={queueId.length === 0}
                        busy={withdraw.isPending}
                        onClick={() => withdraw.mutate({ id: groupId, teamId, queueId })}
                        {...(isCompact ? { style: { flex: 1 } } : {})}
                    >
                        Withdraw
                    </Button>
                </div>
            </div>
            <div role="status" style={{ display: "grid", gap: space[1] }}>
                {lastPush !== undefined && (
                    <Text role="caption" tone="muted">
                        Pushed: {lastPush.addedCount} added, {lastPush.skippedAlreadyMemberCount}{" "}
                        already member.
                    </Text>
                )}
                {lastWithdraw !== undefined && (
                    <Text role="caption" tone="muted">
                        Withdrew: {lastWithdraw.removedCount} removed,{" "}
                        {lastWithdraw.skippedNotMemberCount} not a member.
                    </Text>
                )}
            </div>
            <MutationError error={push.error ?? withdraw.error} />
        </div>
    );
}
